/**
 * The WYSIWYG page preview.
 *
 * ── It renders the SAME components the PDF does ──
 *
 * Not an approximation of the report, and not a second implementation of it: this mounts
 * `<ReportDocument>` from `@pdmg/reporting-blocks`, the component the render worker
 * serialises to HTML before handing it to Chrome. A preview built from its own markup
 * would drift from the artefact on the first template change, and the drift would show up
 * as a client complaint rather than as a failing test.
 *
 * ── Inside a shadow root, deliberately ──
 *
 * The report's stylesheet is written for a printed page and uses names a UI naturally
 * wants too — `.card`, `.row`, `.page`, `.muted`, `.url`. Two of those already collide
 * with this app's own stylesheet. Renaming them would work exactly until the block
 * catalog grows again, and the failure mode is the bad one: an app style silently alters
 * the preview, the preview stops matching the PDF, and nothing fails.
 *
 * A shadow root makes that structurally impossible in both directions. It is also why
 * this uses a nested React root rather than a portal — a portal shares the parent tree's
 * context but still needs a host element, and mounting the root at the shadow boundary
 * keeps the report's own contexts (assets, editing) contained with it.
 */

import * as React from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { ReportDocument, EditContext } from '@pdmg/reporting-blocks';
import type { ResolvedDocument } from '@pdmg/reporting-bindings';
import { paginatePreview } from '../lib/paginate-dom';

/** Chrome for the editor, injected alongside the print stylesheet inside the shadow root. */
const EDITOR_CSS = `
  /* The print stylesheet sets the report's face, size and colour on \`body\`, which has no match inside a
     shadow root — so without this the preview inherits the app's system font and taller line-height,
     wraps differently from the PDF and pushes cards into the footer. Inherited properties are reset to
     what the PDF's bare body has. Roboto itself is loaded document-wide in main.tsx (@font-face does not
     register from inside a shadow root). */
  :host {
    font-family: var(--font-family);
    font-size: var(--size-sm);
    color: var(--color-text);
    line-height: normal;
    font-weight: 400;
    letter-spacing: normal;
  }
  .page {
    box-shadow: 0 1px 3px rgba(0,0,0,.16), 0 8px 24px rgba(0,0,0,.10);
    margin: 0 auto 22px;
    /* background-color, not the \`background\` shorthand — the shorthand resets background-image and
       wipes the V2 wave art off every previewed page. */
    background-color: var(--color-page, #fff);
  }
  /* Flowing pages are split into real page-sized sheets client-side (see paginatePreview), the same way
     the render worker paginates the PDF — so a long table cascades across pages here too. */
  .ev {
    cursor: text;
    border-radius: 2px;
    outline: 1px dashed transparent;
    outline-offset: 2px;
    transition: outline-color .12s, background-color .12s;
  }
  .ev:hover, .ev:focus { outline-color: #7aa7ff; background: rgba(122,167,255,.10); }
  /* Row remove control — builder only; never in the PDF (rendered only when EditContext is present). */
  .row-remove-cell { width: 1.4em; text-align: center; padding: 0 !important; }
  .row-remove {
    border: none; background: transparent; cursor: pointer; line-height: 1;
    font-size: 1.1em; color: #c0271b; padding: 0 .15em; border-radius: 3px;
    opacity: .55; transition: opacity .12s, background-color .12s;
  }
  .row-remove:hover, .row-remove:focus { opacity: 1; background: rgba(214,40,40,.14); outline: none; }
  .ev:focus { outline: 2px solid #2f6bd8; }
  /* An edited figure is marked on the page itself — a reviewer should not have to
     cross-reference a side panel to see what a human changed. */
  .ev-set {
    background: rgba(255,193,7,.20);
    outline-color: #d9a400;
    box-shadow: inset 0 -2px 0 #d9a400;
  }
  /* The last-changed figure of a summing group (device mobile+desktop, or the browsers): green when
     the group now reconciles to total clicks, red when it does not. */
  .ev-ok {
    background: rgba(34,160,90,.20);
    outline-color: #1e874c;
    box-shadow: inset 0 -2px 0 #1e874c;
  }
  .ev-bad {
    background: rgba(214,40,40,.20);
    outline-color: #c0271b;
    box-shadow: inset 0 -2px 0 #c0271b;
  }
`;

export interface ReportCanvasProps {
  doc: ResolvedDocument;
  /**
   * The report being previewed — used to resolve on-demand preview assets (the click heatmap).
   * Optional: the template editor previews sample data with no real report, and simply has no heatmap.
   */
  reportId?: string;
  /** Data paths whose value differs from what the engine generated. */
  changed: ReadonlySet<string>;
  /**
   * Reconciliation status per path, for the last-changed figure of a summing group (device
   * mobile+desktop, browsers): 'ok' = green (sums to total clicks), 'bad' = red (does not).
   */
  reconcile?: Readonly<Record<string, 'ok' | 'bad'>>;
  /** Editing off renders exactly what the PDF renders — no affordances at all. */
  editing: boolean;
  /** Fired with the data path and the element that was activated. */
  onPick: (path: string, el: HTMLElement) => void;
  /** Fired when a row's remove control is activated (CTV app/channel or geo target). */
  onRemove?: (dimension: string, key: string) => void;
  zoom: number;
}

export function ReportCanvas({ doc, reportId, changed, reconcile, editing, onPick, onRemove, zoom }: ReportCanvasProps) {
  const hostRef = React.useRef<HTMLDivElement | null>(null);
  const mountRef = React.useRef<HTMLDivElement | null>(null);
  const shadowRef = React.useRef<ShadowRoot | null>(null);
  // Held in refs so the delegated listener never captures a stale closure.
  const onPickRef = React.useRef(onPick);
  onPickRef.current = onPick;
  const onRemoveRef = React.useRef(onRemove);
  onRemoveRef.current = onRemove;

  React.useEffect(() => {
    const host = hostRef.current;
    if (!host) return;

    /*
     * The shadow root is created once; the React root is created every time.
     *
     * StrictMode deliberately mounts, unmounts and remounts in development. An earlier
     * version guarded the whole effect on "already have a shadow root", which meant the
     * second mount skipped everything — including recreating the React root that the
     * first unmount had just torn down. The canvas came up blank in dev and worked in
     * production, which is the worst way round.
     *
     * `attachShadow` throws if called twice, so that one call is guarded on its own and
     * everything else is rebuilt.
     */
    const shadow = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
    shadowRef.current = shadow;

    /*
     * The report is rendered to STATIC markup (renderToStaticMarkup), not a live React root.
     *
     * The preview paginates a long flowing page into real page-sized sheets by moving nodes (the same
     * measure-then-split the PDF uses); a live React tree would reconcile against those moves and throw.
     * Static markup has no tree to reconcile, and editing works regardless — it is a single delegated
     * listener on the shadow root keyed off `data-edit-path`, which survives any DOM shape.
     */
    const mount = document.createElement('div');
    shadow.appendChild(mount);
    mountRef.current = mount;
    setReady((n) => n + 1);

    /*
     * One delegated listener rather than a handler per value.
     *
     * There are ~90 editable values on a display report and the count grows with the
     * template. Delegation also means the affordance keeps working across a re-render
     * without rebinding, and it is the only way a keyboard activation on a value deep
     * inside a block reaches this component.
     */
    const activate = (e: Event) => {
      const el = e.target as HTMLElement | null;
      // A row remove control takes precedence over an edit (it sits in its own cell, but guard anyway).
      const rem = el?.closest?.('[data-remove-dim]') as HTMLElement | null;
      if (rem) {
        e.preventDefault();
        onRemoveRef.current?.(rem.dataset.removeDim!, rem.dataset.removeKey ?? '');
        return;
      }
      const target = el?.closest?.('[data-edit-path]') as HTMLElement | null;
      if (!target) return;
      e.preventDefault();
      onPickRef.current(target.dataset.editPath!, target);
    };
    const onKey = (e: Event) => {
      const ke = e as KeyboardEvent;
      if (ke.key === 'Enter' || ke.key === ' ') activate(e);
    };
    shadow.addEventListener('click', activate);
    shadow.addEventListener('keydown', onKey);

    return () => {
      shadow.removeEventListener('click', activate);
      shadow.removeEventListener('keydown', onKey);
      mountRef.current = null;
      mount.remove();
    };
  }, []);

  /*
   * Bumped when a React root is created, so the render effect below re-runs against the
   * NEW root after a StrictMode remount rather than silently doing nothing.
   */
  const [ready, setReady] = React.useState(0);

  /*
   * Creative artwork lives on remote ad-serving CDNs. The render worker inlines it as data: URIs
   * (it prints with the network locked down); the preview runs in a real browser, so a creative's
   * URL can simply be its own <img src>. Non-URL asset ids (bundled placeholders) have no preview
   * source here and stay as the block's own placeholder.
   *
   * Production note (see Creative.tsx): once the API serves this SPA it sets `img-src 'self' data:`,
   * which blocks these hosts — the CSP has to name the creative CDNs for the preview to show artwork
   * outside the Vite dev server. In dev there is no such CSP and the images load.
   */
  const resolveAsset = React.useCallback(
    (assetId: string) => {
      if (/^https?:\/\//i.test(assetId)) return assetId;
      /*
       * A `heatmap:<io>` asset is composited server-side (the render worker does it for the PDF; the
       * builder endpoint regenerates the same PNG on demand). The preview points its <img> at that
       * endpoint so the drop-detail overlay and the dedicated heatmap page show the real heatmap
       * instead of nothing. Everything else non-URL (bundled placeholders) has no preview source.
       */
      if (reportId && assetId.startsWith('heatmap:')) {
        const io = assetId.slice('heatmap:'.length);
        return `/v1/builder/${encodeURIComponent(reportId)}/heatmap/${encodeURIComponent(io)}`;
      }
      return null;
    },
    [reportId],
  );

  /*
   * A click heatmap is composited server-side (1–2s the first time), so its <img> in the preview is
   * blank until it arrives — and a blank slot could be mistaken for the finished report. Count the
   * heatmap images that are still loading and show a "generating" indicator until every one has
   * resolved, so what the user sees is only ever the real output or an explicit "still working".
   */
  const [heatmapPending, setHeatmapPending] = React.useState(0);
  const scanHeatmaps = React.useCallback(() => {
    const shadow = shadowRef.current;
    if (!shadow) return;
    let pending = 0;
    for (const img of Array.from(shadow.querySelectorAll('img')) as HTMLImageElement[]) {
      if (!/\/heatmap\//.test(img.getAttribute('src') || '')) continue;
      if (img.complete && img.naturalWidth > 0) continue; // already resolved (loaded or fell back)
      pending += 1;
      // Attach once per (element, src); a rebuild changes the src and re-arms it.
      if (img.dataset.hmWatch !== img.src) {
        img.dataset.hmWatch = img.src;
        const done = () => {
          img.removeEventListener('load', done);
          img.removeEventListener('error', done);
          scanHeatmaps();
        };
        img.addEventListener('load', done);
        img.addEventListener('error', done);
      }
    }
    setHeatmapPending(pending);
  }, []);

  React.useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    let cancelled = false;
    let buildRaf = 0;
    let raf1 = 0;
    let raf2 = 0;

    /*
     * Build the markup and swap it in on the NEXT frame, not inside this synchronous commit.
     *
     * `renderToStaticMarkup` of the whole multi-page report and the `innerHTML` parse that follows are
     * one unbroken main-thread task. Run in the commit, that task froze the page for its whole duration:
     * on a rebuild the "Rebuilding…" spinner stalled and the click that started it only "released" when
     * the swap finished. Deferring one frame lets the busy state paint first and keeps input live right
     * up to the swap. React re-runs this effect — and its cleanup — on every `doc`/edit change, so the
     * pending frame is cancelled and a burst of edits coalesces to a single build of the latest markup
     * instead of a full re-render per keystroke.
     */
    const build = () => {
      if (cancelled || mountRef.current !== mount) return;
      mount.innerHTML = renderToStaticMarkup(
        <EditContext.Provider value={editing ? { overridden: (p) => changed.has(p), reconcile: (p) => reconcile?.[p] } : null}>
          <ReportDocument doc={doc} resolveAsset={resolveAsset} extraCss={EDITOR_CSS} showFooterNotice />
        </EditContext.Provider>
      );

      /*
       * Split flowing pages into real page-sized sheets, like the PDF — AFTER fonts settle, because a row
       * measured against a fallback face is the wrong height and would break on the wrong row. `fonts.ready`
       * resolves immediately once Roboto is loaded. Best-effort: a measurement failure leaves the report
       * unpaginated (one long page) rather than breaking the preview.
       */
      const paginateNow = () => {
        if (cancelled || mountRef.current !== mount) return;
        try { paginatePreview(mount, doc.theme?.page?.heightIn ?? 11); } catch { /* leave unpaginated */ }
        raf1 = requestAnimationFrame(() => { raf2 = requestAnimationFrame(scanHeatmaps); });
      };
      /*
       * `fonts.ready` alone is not enough: it resolves at once when no face has STARTED loading, and Roboto
       * only starts on first use — so the split ran against the fallback face (wider, so long destination
       * URLs wrap) and broke Campaign Details 3+2 where the PDF fits 5. Load the report's weights explicitly,
       * and decode the inlined creatives too (an undecoded <img> measures 0px tall).
       */
      const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
      const family = doc.theme?.typography?.fontFamily ?? 'Roboto';
      const fontsLoaded = fonts
        ? Promise.all([400, 500, 700, 800].map((w) => fonts.load(`${w} 12px "${family}"`))).then(() => fonts.ready)
        : Promise.resolve();
      const imagesDecoded = Promise.all(
        (Array.from(mount.querySelectorAll('img')) as HTMLImageElement[]).map((img) => img.decode().catch(() => undefined)),
      );
      Promise.all([fontsLoaded, imagesDecoded]).catch(() => undefined).then(paginateNow);
    };
    buildRaf = requestAnimationFrame(build);

    return () => { cancelled = true; cancelAnimationFrame(buildRaf); cancelAnimationFrame(raf1); cancelAnimationFrame(raf2); };
  }, [doc, changed, reconcile, editing, ready, resolveAsset, scanHeatmaps]);

  return (
    <>
      {heatmapPending > 0 && (
        <div className="report-canvas-status" role="status" aria-live="polite">
          <svg className="spin" width="15" height="15" viewBox="0 0 24 24" aria-hidden="true">
            <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="3"
              strokeLinecap="round" strokeDasharray="40 100">
              <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12"
                dur="0.8s" repeatCount="indefinite" />
            </circle>
          </svg>
          Generating heatmap…
        </div>
      )}
    <div
      className="report-canvas"
      /*
       * `zoom`, not `transform: scale()`.
       *
       * A transform scales what is painted and leaves the layout box at full size, so a
       * three-page report at 80% reserved the height of three full pages and left a
       * screen of empty grey below the last one. `zoom` reflows, so the scroll extent
       * matches what is on screen.
       *
       * The usual objection to `zoom` — that it is non-standard and rounds differently —
       * does not apply here: this is a preview, and the PDF is produced by the render
       * worker at 100% from the same components. Nothing measured for print goes through
       * this element.
       */
      style={{ zoom }}
      ref={hostRef}
    />
    </>
  );
}
